import { InformationOutline } from "@/components/worksheet/sleep_summaries";
import { Close } from "@mui/icons-material";
import {
  Button,
  ButtonProps,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListProps,
  Stack,
  StackProps,
  Typography,
} from "@mui/material";
import { Fragment, ReactNode, useCallback, useEffect } from "react";
import ReactGA from "react-ga4";

export interface HelpDialogButtonProps extends ButtonProps {}
export function HelpDialogButton(props: HelpDialogButtonProps) {
  return (
    <Button
      startIcon={<InformationOutline />}
      size="small"
      sx={{
        color: "text.secondary",
        fontSize: "14px",
        px: 0,
      }}
      {...props}
    />
  );
}

type DialogOnCloseFunction = Exclude<DialogProps["onClose"], undefined>;
type DialogOnCloseParams = Parameters<DialogOnCloseFunction>;
type DialogOnCloseReturnType = ReturnType<DialogOnCloseFunction>;
type DialogOnCloseEvent = DialogOnCloseParams[0];
type DialogOnCloseReason = DialogOnCloseParams[1];
type HelpDialogOnCloseReason = DialogOnCloseReason | "closeButtonClick";

/**
 * {@link HelpDialogLayout} のプロパティ
 */
export interface HelpDialogLayoutProps
  extends Omit<DialogProps, "scroll" | "onClose"> {
  /** ダイアログのタイトル */
  title: string;
  /** GA の event_label に使用される値。指定しなかった場合、{@link title ダイアログのタイトル} が使用されます。 */
  gaEventLabel?: string;
  /** ダイアログが閉じる際に呼びだされる。 */
  onClose: (
    event: DialogOnCloseEvent,
    reason: HelpDialogOnCloseReason
  ) => DialogOnCloseReturnType;
}

export default function HelpDialogLayout({
  title,
  gaEventLabel = title,
  open,
  onClose,
  PaperProps,
  children,
  ...props
}: HelpDialogLayoutProps) {
  const triggerGA = useCallback(
    ({ name }: { name: "help-dialog.click" | "help-dialog.scroll" }) => {
      const id = process.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID;
      if (id == null || name == null) {
        return;
      }
      ReactGA.initialize(id);
      ReactGA.event(name, {
        category: "help-dialog",
        label: gaEventLabel,
      });
    },
    [gaEventLabel]
  );

  const handleClose = useCallback(
    (event: DialogOnCloseEvent, reason: DialogOnCloseReason) => {
      onClose(event, reason);
    },
    [onClose]
  );
  const handleClickClose = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onClose(event, "closeButtonClick");
    },
    [onClose]
  );

  const handleScrollEvent = useCallback(
    ({
      scrollTop,
      scrollHeight,
      clientHeight,
    }: {
      scrollTop: number;
      scrollHeight: number;
      clientHeight: number;
    }) => {
      const height = scrollHeight - clientHeight;
      if (height === 0) {
        // scroll バーがない場合、scroll イベントを GA へ通知
        return triggerGA({ name: "help-dialog.scroll" });
      }
      if (scrollTop / height >= 0.9) {
        // scroll が画面の 90% を超えた場合、scroll イベントをGAへ通知
        return triggerGA({ name: "help-dialog.scroll" });
      }
    },
    [triggerGA]
  );

  const handleContentLoad = useCallback(
    (elm: HTMLDivElement) => {
      if (elm == null) {
        return;
      }
      handleScrollEvent(elm);
    },
    [handleScrollEvent]
  );

  const handleScroll = useCallback(
    ({ currentTarget }: React.UIEvent<HTMLDivElement, UIEvent>) => {
      handleScrollEvent(currentTarget);
    },
    [handleScrollEvent]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    // ダイアログが開かれた場合、click イベントをGAへ通知
    triggerGA({ name: "help-dialog.click" });
  }, [open, triggerGA]);
  return (
    <Dialog
      {...props}
      open={open}
      onClose={handleClose}
      scroll="paper"
      PaperProps={{
        ...{ sx: { borderRadius: "16px", p: 4, pb: 0 } },
        ...PaperProps,
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ alignItems: "center" }}
        >
          <Typography
            variant="subtitle1"
            sx={{ verticalAlign: "middle", display: "inline-block" }}
          >
            {title}
          </Typography>
          <IconButton onClick={handleClickClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ p: 0, pt: 6, pb: 12 }}
        onScroll={handleScroll}
        ref={handleContentLoad}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function BodyTypographies({ contents }: { contents: ReactNode[] }) {
  return (
    <Typography variant="body2">
      {contents.map((x, i) => (
        <Fragment key={i}>
          {i ? <br /> : null}
          {x}
        </Fragment>
      ))}
    </Typography>
  );
}

export function ContentStack(props: StackProps) {
  return <Stack {...props} gap={6} />;
}

export function SectionStack(props: StackProps) {
  return <Stack {...props} gap={1} />;
}

export function HelpDialogList({ sx, ...props }: ListProps) {
  return (
    <List
      {...props}
      sx={{
        ...{
          listStyleType: "disc",
          ml: 2,
          pl: 2,
          py: 0,
          "& .MuiListItem-root": {
            display: "list-item",
            lineHeight: "21px",
            pl: 2,
            py: 0,
            "&::marker": {
              color: "text.secondary",
              content: "'・'",
              fontSize: "12px",
              fontWeight: 400,
            },
          },
        },
        ...sx,
      }}
    />
  );
}

export function HelpDialogLink({
  href,
  children,
}: {
  href: string;
  children?: ReactNode;
}) {
  return (
    <Link
      href={href}
      rel="noopener noreferrer"
      sx={{
        wordBreak: "break-all",
        color: "#707070",
        fontSize: "12px",
        textDecoration: "underline",
        textDecorationColor: "#707070",
      }}
      target="_blank"
    >
      {children ?? href}
    </Link>
  );
}
